import { ButtonList, Icon, IconButton, Text } from '@dixa/lib-shared-habla';
import * as Popover from '@radix-ui/react-popover';
import classnames from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { AwayStatus, PresenceAgentStatus, presenceApi } from 'apis/presence';
import { featureFlags } from 'utils/featureFlagging/featureFlags';
import { pubSub } from 'utils/pubSub';

import { PresenceDropdownItem, PresenceToggle } from './PresenceDropdownItem';
import styles from './PresenceStatusItem.module.scss';

type PresenceStatusItemProps = {
  awayStatusId?: string;
  oppositeStatus: string;
  status: PresenceAgentStatus;
  isStatusLoading: boolean;
  changeOwnStatus: () => void;
  setCustomAwayStatus: (
    awayStatus: Pick<AwayStatus, 'id' | 'label' | 'emoji'>,
  ) => void;
  showPresenceDropdown: () => void;
  /** DOM element ID for ARIA accessibility (aria-activedescendant). */
  itemId?: string;
  /** The currently focused item's ID (for keyboard navigation visual indicator). */
  focusedItemId?: string;
  /** Whether the "More away statuses" dropdown is open (controlled externally). */
  moreAwayStatusesOpen?: boolean;
  /** Callback when the "More away statuses" dropdown open state changes. */
  onMoreAwayStatusesOpenChange?: (isOpen: boolean) => void;
};

const maxNumberOfVisibleItems = 6;

export const PresenceStatusItem = ({
  awayStatusId,
  isStatusLoading,
  oppositeStatus,
  status,
  changeOwnStatus,
  setCustomAwayStatus,
  showPresenceDropdown,
  itemId,
  focusedItemId,
  moreAwayStatusesOpen,
  onMoreAwayStatusesOpenChange,
}: PresenceStatusItemProps): JSX.Element => {
  const flags = featureFlags.get(['custom-away-status']);
  const [internalIsOpen, setInternalIsOpen] = useState(false);

  // Use controlled state if provided, otherwise use internal state
  const isOpen = moreAwayStatusesOpen ?? internalIsOpen;
  const setIsOpen = useCallback(
    (value: boolean | ((prev: boolean) => boolean)) => {
      const newValue = typeof value === 'function' ? value(isOpen) : value;
      setInternalIsOpen(newValue);
      onMoreAwayStatusesOpenChange?.(newValue);
    },
    [isOpen, onMoreAwayStatusesOpenChange],
  );

  const { awayStatuses } = presenceApi.useReadAwayStatusesQuery(
    { isDeactivated: false },
    {
      skip: !flags.customAwayStatus,
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        awayStatuses: data?.data.customAwayStatuses ?? [],
      }),
    },
  );

  const [firstNStatuses, additionalStatuses] = useMemo(
    () =>
      awayStatuses.length > maxNumberOfVisibleItems
        ? [
            // max - 1 to have at least 2 items in dropdown
            awayStatuses.slice(0, maxNumberOfVisibleItems - 1),
            awayStatuses.slice(maxNumberOfVisibleItems - 1),
          ]
        : [awayStatuses, []],
    [awayStatuses],
  );

  const renderAdditionalAwayStatuses = () => {
    if (!additionalStatuses.length) {
      return [];
    }

    const selectedAdditionalItem = additionalStatuses.find(
      ({ id }) => id === awayStatusId,
    );

    return [
      <PresenceDropdownItem
        key="additional-away-statuses"
        itemId="presence-dropdown-item-more-away-statuses"
        focusedItemId={focusedItemId}
        className={classnames({
          [styles.selected]: !!selectedAdditionalItem,
        })}
        icon={
          selectedAdditionalItem?.emoji ? (
            <>{selectedAdditionalItem.emoji}</>
          ) : (
            'dropdown-list'
          )
        }
        onClick={() => setIsOpen((s) => !s)}
        text={selectedAdditionalItem?.label ?? 'More away statuses'}
        isSubitem
      >
        <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
          <Popover.Trigger asChild>
            <IconButton
              icon="angle-right"
              variant="tertiary"
              aria-label="More away statuses"
            />
          </Popover.Trigger>

          <Popover.Portal>
            <Popover.Content
              className={styles.popover}
              side="right"
              align="start"
              sideOffset={4}
              onOpenAutoFocus={(e) => e.preventDefault()}
            >
              <ButtonList>
                {additionalStatuses.map(({ emoji, id, label }) => {
                  const isSelected = awayStatusId === id;
                  const itemIdForFocus = `presence-dropdown-item-additional-away-status-${id}`;
                  const isFocused = focusedItemId === itemIdForFocus;

                  return (
                    <ButtonList.Item
                      key={id}
                      buttonClassName={classnames(styles.selectable, {
                        [styles.focused]: isFocused,
                      })}
                      isDisabled={isStatusLoading}
                      isSelected={isSelected}
                      onClick={() => setCustomAwayStatus({ id, label, emoji })}
                    >
                      <Text.Paragraph
                        as="span"
                        className={classnames({
                          [styles.selected]: isSelected,
                        })}
                        display="flex"
                      >
                        {emoji}
                        <span className={styles.label}>{label}</span>
                      </Text.Paragraph>
                    </ButtonList.Item>
                  );
                })}
              </ButtonList>
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      </PresenceDropdownItem>,
    ];
  };

  const awayStatusesItems =
    status === 'away'
      ? firstNStatuses
          .map(({ id, emoji, label }) => {
            const isSelected = awayStatusId === id;
            return (
              <PresenceDropdownItem
                key={id}
                itemId={`presence-dropdown-item-away-status-${id}`}
                focusedItemId={focusedItemId}
                icon={<>{emoji}</>}
                text={label}
                isSubitem
                className={classnames(styles.selectable, {
                  [styles.selected]: isSelected,
                })}
                disabled={isStatusLoading}
                onClick={() => setCustomAwayStatus({ id, label, emoji })}
              >
                {isSelected ? (
                  <Icon name="done" size={20} color="secondary" />
                ) : undefined}
              </PresenceDropdownItem>
            );
          })
          .concat(renderAdditionalAwayStatuses())
      : undefined;

  useEffect(() => {
    const unsubscribe = pubSub.subscribe('presenceAwayStatusSet', () => {
      // Open presence dropdown only if awayStatuses are available.
      if (awayStatuses.length) {
        showPresenceDropdown();
      }
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awayStatuses]);

  return (
    <PresenceDropdownItem
      itemId={itemId}
      focusedItemId={focusedItemId}
      icon="working"
      text={status}
      tooltipText={`Set ${oppositeStatus}`}
      descriptionTestId="presence-dropdown-status"
      subitems={awayStatusesItems}
      isSubchannelOpen
    >
      <PresenceToggle
        id="working-status"
        active={status === 'working'}
        onToggle={changeOwnStatus}
      />
    </PresenceDropdownItem>
  );
};
